//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';

import FacebookIcon from '@material-ui/icons/Facebook';
import PinterestIcon from '@material-ui/icons/Pinterest';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import LinkIcon from '@material-ui/icons/Link';
import CloseIcon from '@material-ui/icons/Close';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { copyTextToClipboard } from '../../system/helpers/systemHelper';
import { AppContext } from '../../common/contexts';
import {
    getTwitterShareLink,
    getLinkedInShareLink,
    getPinterestShareLink,
    getFacebookShareLink,
    getShareWindowConfigStr,
    getEmployerShareUrl,
    getJobSeekerShareUrl,
} from '../../common/linkHelper';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { USER_TYPE_EMP, USER_TYPE_JS } from '../../common/constants';
import XIcon from '../../assets/x_logo_black.png';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdIconButton from '../../common/elemental/AfitdIconButton';
import AfitdSnackbar from '../../common/elemental/AfitdSnackbar';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------

const styles = theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
    },
    shareButton: {
        // background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
        // borderRadius: 3,
        // border: 0,
        // color: 'white',
        height: 24,
        // padding: '0 30px',
        marginLeft: '10px',
    },
    tweetButton: {
        height: 24,
    },
    pinButton: {
        height: 24,
    },
    copyButton: {
        height: 24,
    },

    facebookIcon: {
        color: '#3B5998',
    },
    xIcon: {
        color: '#000',
    },
    pinterestIcon: {
        color: '#BD081C',
    },
    linkedInIcon: {
        color: '#2867B2',
    },
    closeIcon: {
        fontSize: '18px',
    },
});

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { props, shareUrl, snackbarMessage, setSnackbarMessage, handleCloseSnackbar } = params;

    const { classes, onClose = handleClickCloseButton } = props;
    const { shareButton: shareButtonCls } = classes;

    const defns = {
        shareButton: {
            startIcon: <FacebookIcon className={classes.facebookIcon} />,
            label: 'Share',
            fullWidth: false,
            size: 'small',
            onClick: handleClickShareButton.bind(null, shareUrl),
            classes: {
                root: shareButtonCls,
            },
        },
        tweetButton: {
            startIcon: <img src={XIcon} alt="x_icon" width="12px" style={{ marginLeft: 6 }} />,
            fullWidth: false,
            size: 'small',
            onClick: handleClickTweetButton.bind(null, shareUrl),
            classes: {
                root: shareButtonCls,
            },
        },
        pinButton: {
            startIcon: <PinterestIcon className={classes.pinterestIcon} />,
            label: 'Pin',
            fullWidth: false,
            size: 'small',
            onClick: handleClickPinButton.bind(null, shareUrl),
            classes: {
                root: shareButtonCls,
            },
        },
        linkedInButton: {
            startIcon: <LinkedInIcon className={classes.linkedInIcon} />,
            label: 'LinkedIn',
            fullWidth: false,
            size: 'small',
            onClick: handleClickLinkedInButton.bind(null, shareUrl),
            classes: {
                root: shareButtonCls,
            },
        },
        copyButton: {
            startIcon: <LinkIcon />,
            label: 'Copy',
            fullWidth: false,
            size: 'small',
            onClick: handleClickCopyButton.bind(null, shareUrl, setSnackbarMessage),
            classes: {
                root: shareButtonCls,
            },
        },
        closeIconButton: {
            size: 'small',
            onClick: onClose,
        },
        snackbar: {
            message: { successMessage: snackbarMessage },
            onClose: handleCloseSnackbar,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickShareButton = shareUrl => {
    window.open(getFacebookShareLink(shareUrl), '_blank', getShareWindowConfigStr());
};

const handleClickTweetButton = shareUrl => {
    window.open(getTwitterShareLink(shareUrl), '_blank', getShareWindowConfigStr());
};

const handleClickPinButton = (shareUrl, shareMediaUrl) => {
    window.open(getPinterestShareLink(shareUrl, shareMediaUrl), '_blank', getShareWindowConfigStr());
};

const handleClickLinkedInButton = shareUrl => {
    window.open(getLinkedInShareLink(shareUrl), '_blank', getShareWindowConfigStr());
};

const handleClickCopyButton = (shareUrl, setSnackbarMessage) => {
    copyTextToClipboard(shareUrl, setSnackbarMessage);
    // console.log('%c@@@@ handleClickCopyButton START', 'background: #222; color: #bada55');
};

const handleClickCloseButton = () => {
    // console.log('%c@@@@ handleClickCloseButton START', 'background: #222; color: #bada55');
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
// Since this component is used in the MUI Slide, it has to be able to hold a ref.
//   Therefore, we cannot use function component.
class ShareButtonBar extends Component {
    state = {
        snackbarMessage: null,
    };

    setSnackbarMessage = message => {
        this.setState({ snackbarMessage: message });
    };

    handleCloseSnackbar = () => {
        this.setState({ snackbarMessage: null });
    };

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    render() {
        //---------------------------------------------------------------------
        // From context
        //---------------------------------------------------------------------
        const appContext = this.context;
        const { currentUser, userId, currentEmployer, employerId } = appContext;
        const isEmp = _get(currentUser, 'type', USER_TYPE_JS) === USER_TYPE_EMP;

        //---------------------------------------------------------------------
        // From props
        //---------------------------------------------------------------------

        //---------------------------------------------------------------------
        // Derived
        //---------------------------------------------------------------------
        const urlName = isEmp ? _get(currentEmployer, 'urlName') : _get(currentUser, 'userName');
        const shareUrl = isEmp ? getEmployerShareUrl({ urlName, employerId }) : getJobSeekerShareUrl({ urlName, userId });

        //---------------------------------------------------------------------
        // Defns
        //---------------------------------------------------------------------
        const defns = getDefns({
            props: this.props,
            shareUrl,
            snackbarMessage: this.state.snackbarMessage,
            setSnackbarMessage: this.setSnackbarMessage,
            handleCloseSnackbar: this.handleCloseSnackbar,
        });

        return (
            <div {...defns.root}>
                {this.props.isEmp && <AfitdButton {...defns.shareButton}>Share</AfitdButton>}
                <AfitdButton {...defns.tweetButton}>Post</AfitdButton>
                {/* <AfitdButton {...defns.pinButton}>Pin</AfitdButton> */}
                <AfitdButton {...defns.linkedInButton}>LinkedIn</AfitdButton>
                <AfitdButton {...defns.copyButton}>Copy</AfitdButton>
                <AfitdIconButton {...defns.closeIconButton}>
                    <CloseIcon {...defns.closeIcon} />
                </AfitdIconButton>
                <AfitdSnackbar {...defns.snackbar} />
            </div>
        );
    }
}

ShareButtonBar.contextType = AppContext;

export default withStyles(styles)(ShareButtonBar);
